import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { formatDateTime } from '@/utils/format';
import { supportSessionExpired, useSupportSession } from './supportSession';
import styles from './platform-ui.module.css';

/**
 * The banner that says somebody is inside a customer's institution right now.
 *
 * NO CLOSE BUTTON, AND THERE IS NO PROP THAT ADDS ONE.
 * Every dismissible warning is dismissed. A support engineer working a ticket for
 * forty minutes will close a banner in the first thirty seconds and then spend the
 * rest of the session with nothing on screen distinguishing "I am looking at my
 * own console" from "I am acting as the bursar of a school in Douala". That
 * confusion is not hypothetical; it is the entire reason PA-17 exists, and it is
 * why the server stores `impersonationBanner` as a column that no API can set to
 * false rather than as something the client decides.
 *
 * It renders for READ_ONLY sessions too, with different words. Read-only access is
 * still access to somebody else's data, and the engineer should know which mode
 * they are in without checking — read-only is also the mode where an attempted
 * write will simply be refused, and a refusal is much easier to understand when
 * the screen has already said the session cannot write.
 *
 * The remaining time counts down. A grant with eight minutes left is a different
 * situation from one with four hours, and an expiry timestamp alone makes the
 * reader do arithmetic they will get wrong.
 */
export function ImpersonationBanner() {
  const { t, i18n } = useTranslation();
  const session = useSupportSession();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!session) return;
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [session]);

  if (!session) return null;

  const expired = supportSessionExpired(session);
  const minutesLeft = Math.max(0, Math.round((new Date(session.expiresAt).getTime() - now) / 60_000));
  const impersonating = session.mode === 'IMPERSONATION';

  return (
    <div
      className={`${styles.impersonation} ${impersonating ? styles.impersonationHard : ''}`}
      // `alert` rather than `status`: this must interrupt a screen-reader user, not
      // wait politely for a pause. It is the only thing on the page that changes
      // what every subsequent action means.
      role="alert"
      aria-live="assertive"
      data-mode={session.mode}
    >
      <span className={styles.impersonationIcon} aria-hidden="true">
        <Icon name={impersonating ? 'warning' : 'noAccess'} size={18} />
      </span>
      <div className={styles.impersonationText}>
        <p className={styles.impersonationTitle}>
          {impersonating ? t('platform.support.banner.impersonating') : t('platform.support.banner.readOnly')}
        </p>
        <p className={styles.impersonationBody}>
          {t('platform.support.banner.grant', { id: session.grantId })}
          {' · '}
          {expired
            ? t('platform.support.banner.expired')
            : t('platform.support.banner.remaining', {
                minutes: minutesLeft,
                until: formatDateTime(session.expiresAt, i18n.language),
              })}
        </p>
        {session.readOnly && <p className={styles.impersonationBody}>{t('platform.support.banner.readOnlyNote')}</p>}
      </div>
      <span className={styles.impersonationTag}>{t('platform.support.banner.recorded')}</span>
    </div>
  );
}
